// src/components/ui/Skeleton.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Reusable skeleton loading components.
// Uses a shimmer animation (left-to-right shine) that matches modern platforms.
//
// Usage:
//   import { Skeleton, SkeletonCard, SkeletonTable } from '../ui/Skeleton';
//
//   <Skeleton className="h-4 w-32" />                    // single bar
//   <SkeletonCard />                                      // KPI card
//   <SkeletonTable rows={5} cols={4} />                  // data table
//   <SkeletonDashboard />                                 // full dashboard
// ─────────────────────────────────────────────────────────────────────────────

// ── Base shimmer bar ──────────────────────────────────────────────────────────
export function Skeleton({ className = '', rounded = 'rounded-md' }) {
  return (
    <div
      className={`relative overflow-hidden bg-muted ${rounded} ${className}`}
      aria-hidden="true"
    >
      {/* Shimmer sweep */}
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </div>
  );
}

// ── KPI card skeleton ─────────────────────────────────────────────────────────
export function SkeletonCard({ accent = true }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-4 relative overflow-hidden">
      {accent && <div className="absolute top-0 left-0 right-0 h-0.5 bg-muted" />}
      <Skeleton className="h-3 w-20 mb-3" />
      <Skeleton className="h-7 w-28 mb-2" />
      <Skeleton className="h-5 w-14 rounded-full" />
    </div>
  );
}

// ── Chart skeleton ────────────────────────────────────────────────────────────
export function SkeletonChart({ height = 'h-[200px]' }) {
  return (
    <div className={`relative ${height} flex items-end gap-1.5 px-2 pb-2`}>
      {/* Y axis */}
      <div className="absolute left-0 top-0 bottom-6 w-8 flex flex-col justify-between pb-1">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-2.5 w-6" />
        ))}
      </div>
      {/* Bars / line simulation */}
      <div className="flex-1 ml-10 flex items-end gap-1 h-full pb-6">
        {[65, 45, 80, 55, 90, 40, 70, 85, 50, 75, 60, 95].map((h, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-t-sm"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      {/* X axis labels */}
      <div className="absolute bottom-0 left-10 right-0 flex justify-between">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-2 w-6" />
        ))}
      </div>
    </div>
  );
}

// ── Table skeleton ────────────────────────────────────────────────────────────
export function SkeletonTable({ rows = 5, cols = 4 }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3 flex gap-4 bg-muted/30">
        {[...Array(cols)].map((_, i) => (
          <Skeleton key={i} className="h-3" style={{ width: `${60 + (i % 3) * 20}px` }} />
        ))}
      </div>
      {/* Rows */}
      {[...Array(rows)].map((_, row) => (
        <div key={row} className="border-b border-border last:border-0 px-4 py-3.5 flex gap-4 items-center">
          {[...Array(cols)].map((_, col) => (
            <Skeleton
              key={col}
              className="h-3.5"
              style={{ width: col === 0 ? '140px' : `${50 + ((row + col) % 4) * 15}px` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Source card skeleton (Publisher / ad platform) ────────────────────────────
export function SkeletonSourceCard() {
  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-3 w-16" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i}>
            <Skeleton className="h-2.5 w-14 mb-1.5" />
            <Skeleton className="h-5 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Full dashboard skeleton ───────────────────────────────────────────────────
export function SkeletonDashboard() {
  return (
    <div className="space-y-6 pb-6" aria-label="Loading dashboard…">
      {/* Hero panel */}
      <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-3 w-24 bg-white/10" />
            <Skeleton className="h-8 w-56 bg-white/10" />
            <Skeleton className="h-3 w-36 bg-white/10" />
          </div>
          <Skeleton className="h-10 w-28 rounded-xl bg-white/10" />
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Skeleton className="flex-1 h-1.5 rounded-full bg-white/10" />
          <Skeleton className="h-3 w-20 bg-white/10" />
        </div>
      </div>

      {/* KPI cards — 3 on mobile, 6 on desktop */}
      <div>
        <Skeleton className="h-3 w-32 mb-3" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-4 w-28" />
          <div className="flex gap-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-7 w-16 rounded-lg" />)}
          </div>
        </div>
        <SkeletonChart height="h-[200px]" />
      </div>

      {/* Two-col: source cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => <SkeletonSourceCard key={i} />)}
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-36 mb-4" />
        <SkeletonTable rows={4} cols={5} />
      </div>
    </div>
  );
}

// ── Inline list skeleton ──────────────────────────────────────────────────────
export function SkeletonList({ rows = 4 }) {
  return (
    <div className="space-y-2">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border">
          <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5" style={{ width: `${50 + (i % 3) * 15}%` }} />
            <Skeleton className="h-2.5 w-24" />
          </div>
          <Skeleton className="h-6 w-14 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export default Skeleton;

// ── Ad platform page skeleton (Meta, Google, TikTok) ─────────────────────────
export function SkeletonAdPlatform() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      {/* Header strip */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-3.5 w-56" />
        </div>
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-9 w-24 rounded-lg" />)}
        </div>
      </div>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[...Array(5)].map((_, i) => <SkeletonCard key={i} accent={false} />)}
      </div>
      {/* Chart */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-7 w-28 rounded-lg" />
        </div>
        <SkeletonChart height="h-[180px]" />
      </div>
      {/* Accounts table */}
      <SkeletonTable rows={3} cols={5} />
    </div>
  );
}

// ── AdSense / AdManager skeleton ─────────────────────────────────────────────
export function SkeletonPublisherPlatform() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-3.5 w-60" />
        </div>
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-9 w-24 rounded-lg" />)}
        </div>
      </div>
      {/* KPI cards — 4 col */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} accent={false} />)}
      </div>
      {/* Two-col: chart + quick stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-5">
          <Skeleton className="h-4 w-36 mb-4" />
          <SkeletonChart height="h-[200px]" />
        </div>
        <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
          <Skeleton className="h-4 w-28 mb-2" />
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Publisher dashboard skeleton ──────────────────────────────────────────────
export function SkeletonPublisherDashboard() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="space-y-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-3.5 w-64" />
      </div>
      {/* Source cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => <SkeletonSourceCard key={i} />)}
      </div>
      {/* Chart */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-40 mb-4" />
        <SkeletonChart height="h-[200px]" />
      </div>
      {/* Monthly table */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-4 w-36" />
          <div className="flex gap-1">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-7 w-12 rounded-lg" />)}
          </div>
        </div>
        <SkeletonTable rows={8} cols={4} />
      </div>
    </div>
  );
}

// ── Ecommerce dashboard skeleton ──────────────────────────────────────────────
export function SkeletonEcommerce() {
  return (
    <div className="space-y-5 p-5" aria-label="Loading…">
      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {[...Array(9)].map((_, i) => <SkeletonCard key={i} accent={false} />)}
      </div>
      {/* Chart */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-32 mb-4" />
        <SkeletonChart height="h-[240px]" />
      </div>
      {/* Two-col bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border p-5">
          <Skeleton className="h-4 w-32 mb-4" />
          <SkeletonTable rows={4} cols={3} />
        </div>
        <div className="bg-card rounded-2xl border border-border p-5">
          <Skeleton className="h-4 w-36 mb-4" />
          <SkeletonTable rows={4} cols={3} />
        </div>
      </div>
    </div>
  );
}

// ── Shopify skeleton ──────────────────────────────────────────────────────────
export function SkeletonShopify() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="space-y-1.5">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-3.5 w-64" />
      </div>
      {/* Tab bar */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        <Skeleton className="h-8 w-20 rounded-lg" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      {/* Store cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="bg-card rounded-xl border border-border overflow-hidden">
            <Skeleton className="h-2 w-full rounded-none" />
            <div className="p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3.5 w-44" />
                </div>
                <Skeleton className="h-8 w-8 rounded-lg" />
              </div>
              {[...Array(3)].map((_, j) => (
                <div key={j} className="flex justify-between">
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-24" />
                </div>
              ))}
              <div className="flex gap-2 pt-1 border-t border-border">
                <Skeleton className="flex-1 h-9 rounded-lg" />
                <Skeleton className="h-9 w-10 rounded-lg" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Custom Domains skeleton ───────────────────────────────────────────────────
export function SkeletonCustomDomains() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="flex items-center justify-between">
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-3.5 w-56" />
        </div>
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
      {/* Info banner */}
      <Skeleton className="h-20 w-full rounded-xl" />
      {/* Domain rows */}
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <div className="flex gap-2">
                {[...Array(2)].map((_, j) => <Skeleton key={j} className="h-8 w-20 rounded-lg" />)}
              </div>
            </div>
            <div className="space-y-1.5 ml-7">
              {[...Array(2)].map((_, j) => <Skeleton key={j} className="h-3 w-72" />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Projects list skeleton ────────────────────────────────────────────────────
export function SkeletonProjects() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-28" />
          <Skeleton className="h-3.5 w-64" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
      </div>
      <div className="flex gap-3">
        <Skeleton className="h-9 flex-1 rounded-lg" />
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-5 w-36" />
                <Skeleton className="h-3.5 w-48" />
              </div>
              <Skeleton className="h-7 w-7 rounded-lg flex-shrink-0" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Project detail skeleton ───────────────────────────────────────────────────
export function SkeletonProjectDetail() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-3.5 w-32" />
        </div>
      </div>
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} accent={false} />)}
      </div>
      {/* Tests list */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-28 rounded-lg" />
        </div>
        <SkeletonTable rows={4} cols={5} />
      </div>
      {/* Pages list */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-28 rounded-lg" />
        </div>
        <SkeletonTable rows={3} cols={4} />
      </div>
    </div>
  );
}

// ── Test detail skeleton ──────────────────────────────────────────────────────
export function SkeletonTestDetail() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <div className="space-y-1.5 flex-1">
          <Skeleton className="h-6 w-52" />
          <Skeleton className="h-3.5 w-36" />
        </div>
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      {/* Status + config cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} accent={false} />)}
      </div>
      {/* Variants */}
      <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-28 rounded-lg" />
        </div>
        {[...Array(2)].map((_, i) => (
          <div key={i} className="border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[...Array(3)].map((_, j) => (
                <div key={j} className="space-y-1">
                  <Skeleton className="h-3 w-14" />
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
      {/* Chart */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-36 mb-4" />
        <SkeletonChart height="h-[180px]" />
      </div>
    </div>
  );
}

// ── Landing pages list skeleton ───────────────────────────────────────────────
export function SkeletonLandingPages() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-36" />
          <Skeleton className="h-3.5 w-56" />
        </div>
        <Skeleton className="h-9 w-36 rounded-lg" />
      </div>
      {/* Recent pages */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
            <Skeleton className="h-3 w-20" />
            <div className="flex gap-3">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-14" />
            </div>
          </div>
        ))}
      </div>
      {/* Project sections */}
      {[...Array(2)].map((_, i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <SkeletonTable rows={3} cols={5} />
        </div>
      ))}
    </div>
  );
}

// ── Custom accounts skeleton ──────────────────────────────────────────────────
export function SkeletonCustomAccounts() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-3.5 w-60" />
        </div>
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} accent={false} />)}
      </div>
      {/* Accounts list */}
      <SkeletonList rows={4} />
      {/* Chart */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-32 mb-4" />
        <SkeletonChart height="h-[160px]" />
      </div>
    </div>
  );
}

// ── Team page skeleton ────────────────────────────────────────────────────────
export function SkeletonTeam() {
  return (
    <div className="space-y-6" aria-label="Loading…">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-3.5 w-56" />
        </div>
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
      {/* Member rows */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b">
          <Skeleton className="h-4 w-24" />
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0">
            <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-8 w-20 rounded-lg" />
          </div>
        ))}
      </div>
      {/* Invite section */}
      <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
        <Skeleton className="h-4 w-28" />
        <div className="flex gap-3">
          <Skeleton className="h-10 flex-1 rounded-lg" />
          <Skeleton className="h-10 w-28 rounded-lg" />
        </div>
      </div>
    </div>
  );
}
